/**
 * PRX-139 — the calendar's closed lists, mirrored from the server's single
 * source of truth `backend-v2/src/appointments/appointments.constants.ts`
 * (`appointments.drift.spec.ts` reads it as text — change both together).
 */
export const APPOINTMENT_TYPES = [
  'medscheck',
  'counselling',
  'vaccination',
  'minor_ailment',
  'pickup_delivery',
  'staff_meeting',
  'day_off',
  'other',
] as const;
export type AppointmentType = (typeof APPOINTMENT_TYPES)[number];

/** Sentence case; MedsCheck is the programme's own name. */
export const APPOINTMENT_TYPE_LABELS: Record<AppointmentType, string> = {
  medscheck: 'MedsCheck',
  counselling: 'Counselling',
  vaccination: 'Vaccination',
  minor_ailment: 'Minor ailment',
  pickup_delivery: 'Pickup / delivery',
  staff_meeting: 'Staff meeting',
  day_off: 'Day off',
  other: 'Other',
};

/** Never carry a patient (the server refuses one). */
export const STAFF_ONLY_APPOINTMENT_TYPES = ['staff_meeting', 'day_off'] as const;

/**
 * Need a patient (the server refuses none). An appointment stored with no
 * patient before 2026-10-13 keeps its type and stays editable (PRX-139 Q3).
 */
export const PATIENT_REQUIRED_APPOINTMENT_TYPES = ['medscheck', 'counselling', 'vaccination', 'minor_ailment'] as const;

/** The `code` on the 409 that asks for the overlap warnings to be confirmed (PRX-139 Q4). */
export const APPOINTMENT_OVERLAP_CODE = 'APPOINTMENT_OVERLAP';

export const APPOINTMENT_STATUSES = ['scheduled', 'cancelled'] as const;
export type AppointmentStatus = (typeof APPOINTMENT_STATUSES)[number];

export const APPOINTMENT_TITLE_MAX = 200;
export const APPOINTMENT_NOTES_MAX = 2000;
export const APPOINTMENT_STAFF_MAX = 20;
export const APPOINTMENT_RANGE_MAX_DAYS = 62;

/** HH:mm, 00:00 to 23:59 — the server's pattern. */
export const APPOINTMENT_TIME_PATTERN = /^([01][0-9]|2[0-3]):[0-5][0-9]$/;

/** The PRX-54 visibility key behind the sidebar's Calendar entry. */
export const CALENDAR_VISIBILITY_KEY = 'sidebar.item.calendar';

/**
 * A chip colour per type, from the theme palette (no hard-coded hex): the
 * patient services in the brand blues, staff time in neutral grey.
 */
export const APPOINTMENT_TYPE_COLOR: Record<AppointmentType, 'primary' | 'info' | 'secondary' | 'success' | 'warning' | 'default'> = {
  medscheck: 'primary',
  counselling: 'info',
  vaccination: 'success',
  minor_ailment: 'secondary',
  pickup_delivery: 'warning',
  staff_meeting: 'default',
  day_off: 'default',
  other: 'default',
};
